import React, { useState, useContext } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { Text, Stack } from '@atlaskit/primitives/compiled';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { expVal } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	VIEW_JSM_BANNER,
	DISABLE_JSM_PERMISSIONS,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceSuccess,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useExperimentalQueryStateActions } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQueryState.ts';
import { generateCacheKey } from '@confluence/legacy-schema-bridge/entry-points/generateCacheKey.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';

import { useExperimentalUnlicensedPermissionQuery } from '../graphql/useExperimentalUnlicensedPermissionQuery.ts';
import { useExperimentalRemoveUnlicensedPermissionMutation } from '../graphql/useExperimentalRemoveUnlicensedPermissionMutation.ts';

const i18n = defineMessages({
	infoBoxTitle: {
		id: 'space-permissions.jsm-prompt.info-box-title',
		defaultMessage: 'Any active user can view this space',
		description: 'The title of the JSM Knowledge base prompt message box.',
	},
	infoBoxDescription: {
		id: 'space-permissions.jsm-prompt.info-box-description',
		defaultMessage:
			'Any active user can view content in this space, including users who don’t have a Confluence license. This was enabled through Jira Service Management so people can view knowledge articles when raising a help request.',
		description: 'The description of the JSM Knowledge base prompt message box.',
	},
	infoBoxButtonLink: {
		id: 'space-permissions.jsm-prompt.info-box-button',
		defaultMessage: 'Disable this permission',
		description:
			'The button on the JSM Knowledge base prompt message box to disable the permission.',
	},
	modalTitle: {
		id: 'space-permissions.jsm-prompt.modal-title',
		defaultMessage: 'Disable this permission for all active users',
		description:
			'The warning modal title after clicking on the button link on the JSM Knowledge base message box.',
	},
	modalIntroParagraph: {
		id: 'space-permissions.jsm-prompt.modal-intro-paragraph',
		defaultMessage: `This space is linked to a project in Jira Service Management so users can view knowledge articles when they raise requests.`,
		description: 'The modal content to explain the situation of this space on the warning modal.',
	},
	modalActionParagraph: {
		id: 'space-permissions.jsm-prompt.modal-action-paragraph',
		defaultMessage: `If you disable this permission, it can only be enabled again through Jira Service Management.`,
		description:
			'The modal content to explain the results of the disabling the permissions on this modal.',
	},
	modalPrimaryAction: {
		id: 'space-permissions.jsm-prompt.modal-primary-action',
		defaultMessage: 'Disable',
		description: 'The primary action of the JSM warning modal to disable the permission.',
	},
	modalCancelAction: {
		id: 'space-permissions.jsm-prompt.modal-cancel-action',
		defaultMessage: 'Cancel',
		description: 'The button to cancel and close the modal.',
	},
	modalIntroParagraphGalaxia: {
		id: 'space-permissions.jsm-prompt.modal-intro-paragraph-galaxia',
		defaultMessage: `This space is linked to a Jira Service Management space so users can view knowledge articles when they raise requests.`,
		description: 'The modal content to explain the situation of this space on the warning modal.',
	},
});

interface JSMPromptProps {
	spaceKey: string;
}

export const JSMPrompt: FC<JSMPromptProps> = ({ spaceKey }) => {
	const [isOpen, setOpen] = useState<boolean>(false);
	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { Modal } = useChoreographerAssets();
	const { setQueryState } = useExperimentalQueryStateActions();

	const { data, error } = useExperimentalUnlicensedPermissionQuery({
		variables: { spaceKey },
	});

	const removeUnlicensedPermissionMutationOptions = {
		update(_: any) {
			// For experimental mutation, update the useExperimentalQuery cache
			// The cache key matches the URL and variables from the query
			const cacheKey = generateCacheKey({
				url: `/wiki/rest/api/space?spaceKey=${spaceKey}&expand=permissions`,
				method: 'GET',
				headers: {
					'Content-Type': MediaType.JSON,
				},
				variables: { spaceKey },
			});

			setQueryState(cacheKey, {
				data: {
					experimentalUnlicensedPermission: {
						unlicensedPermissionId: null,
					},
				},
				loading: false,
				error: undefined,
				called: true,
			});
		},
	};

	const [removeUnlicensedPermission] = useExperimentalRemoveUnlicensedPermissionMutation(
		removeUnlicensedPermissionMutationOptions,
	);

	if (error) {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PERMISSIONS_EXPERIENCE,
		});
		//Fail View Experience
		experienceTracker.fail({
			name: VIEW_JSM_BANNER,
			error,
		});
	}

	const onEditPermsClicked = () => {
		setOpen(true);
		//Start Disable JSM Experience
		experienceTracker.start({
			name: DISABLE_JSM_PERMISSIONS,
		});
	};

	const closeModal = () => {
		setOpen(false);
	};

	const onDisableClicked = async () => {
		try {
			await removeUnlicensedPermission({
				variables: {
					spaceKey,
					permissionId: data?.experimentalUnlicensedPermission?.unlicensedPermissionId || 0,
				},
				...removeUnlicensedPermissionMutationOptions,
			});
		} catch (error) {
			//Fail Disable JSM Experience
			experienceTracker.fail({
				name: DISABLE_JSM_PERMISSIONS,
				error,
			});
			markErrorAsHandled(error);
		} finally {
			// await updateUnlicensedPermission();
			setOpen(false);
		}
		//Succeed Disable JSM Experience
		experienceTracker.succeed({
			name: DISABLE_JSM_PERMISSIONS,
		});
	};

	return (
		<>
			{data?.experimentalUnlicensedPermission?.unlicensedPermissionId ? (
				<>
					<ExperienceStart name={VIEW_JSM_BANNER} />
					<SectionMessage
						title={formatMessage(i18n.infoBoxTitle)}
						actions={[
							<SectionMessageAction href="#" key="disable-btn" onClick={onEditPermsClicked}>
								{formatMessage(i18n.infoBoxButtonLink)}
							</SectionMessageAction>,
						]}
					>
						<Text as="p">{formatMessage(i18n.infoBoxDescription)}</Text>
						<ExperienceSuccess name={VIEW_JSM_BANNER} />
					</SectionMessage>
					<ModalTransition>
						{isOpen && (
							<Modal onClose={closeModal}>
								<ModalHeader hasCloseButton>
									<ModalTitle>{formatMessage(i18n.modalTitle)}</ModalTitle>
								</ModalHeader>
								<ModalBody>
									<Stack space="space.150">
										<Text as="p">
											{formatMessage(
												expVal('project-terminology-refresh', 'isEnabled', false)
													? i18n.modalIntroParagraphGalaxia
													: i18n.modalIntroParagraph,
											)}
										</Text>
										<Text as="p">{formatMessage(i18n.modalActionParagraph)}</Text>
									</Stack>
								</ModalBody>
								<ModalFooter>
									<Button appearance="primary" onClick={onDisableClicked}>
										{formatMessage(i18n.modalPrimaryAction)}
									</Button>
									<Button appearance="subtle" onClick={closeModal}>
										{formatMessage(i18n.modalCancelAction)}
									</Button>
								</ModalFooter>
							</Modal>
						)}
					</ModalTransition>
				</>
			) : null}
		</>
	);
};
